import { styled, IconButton, Button, Paper } from "@mui/material";
import useThemeStore from "../../services/store/useThemeStore";

// --------------------
// 1) Custom Gradient Checkbox
// --------------------
import Checkbox, { checkboxClasses } from "@mui/material/Checkbox";
// Use a custom gradient or brand color for the checked state
export const GradientCheckbox = styled(Checkbox)(({ theme }) => {
  const { darkMode } = useThemeStore();

  return {
    [`&.${checkboxClasses.checked}`]: {
      color: darkMode ? "#fbbf24" : "#e69100",
    },
    color: darkMode ? "var(--neutral-400)" : undefined,
  };
});

// --------------------
// 2) Custom Gradient Button
// --------------------
export const GradientButton = styled("button")(() => {
  const { darkMode } = useThemeStore();

  return {
    background: "linear-gradient(90deg, #f9a825, #ea580c)",
    color: "#ffffff",
    font: "var(--semibold-md)",
    textTransform: "none",
    border: "none",
    borderRadius: "var(--spacing-3x)",
    padding: "var(--spacing-3x) var(--spacing-5x)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    marginTop: "10px",
    "&:hover": {
      background: "linear-gradient(90deg, #ea580c, #f9a825)",
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  };
});

// --------------------
// 3) Custom Input Field & Icons
// --------------------
export const InputField = styled("div")(() => ({
  width: "100%",
  maxWidth: "560px",
  marginBottom: "8px",
  position: "relative",
  display: "flex",
  flexDirection: "column",
}));

export const InputFieldLabel = styled("div")<{ color?: string }>(({
  color,
}) => {
  const { darkMode } = useThemeStore();

  return {
    font: "var(--body-sm)",
    textAlign: "left",
    marginBottom: "8px",
    color: color || (darkMode ? "#fbbf24" : "#0f172a"),
  };
});

// Wrap the input + icon in one container for alignment
export const InputWrapper = styled("div")(() => ({
  position: "relative",
}));

export const InputIcon = styled("div")(() => {
  const { darkMode } = useThemeStore();

  return {
    position: "absolute",
    left: "12px",
    top: "55%",
    transform: "translateY(-50%)",
    color: darkMode ? "var(--neutral-400)" : "var(--auth-text-gray-light)",
  };
});

export const Input = styled("input")(() => {
  const { darkMode } = useThemeStore();

  return {
    width: "100%",
    height: "44px",
    font: "var(--body-md)",
    padding: "var(--spacing-3x)",
    paddingLeft: "42px", // to avoid overlapping with icon
    borderRadius: "8px",
    border: darkMode
      ? "1px solid var(--neutral-600)"
      : "1px solid var(--neutral-200)",
    background: darkMode ? "var(--neutral-700)" : "var(--neutral-50)",
    color: darkMode ? "var(--neutralWhite)" : undefined,
    outline: "none",
    transition: "0.3s",
    "&:focus": {
      borderColor: darkMode ? "#f59e0b" : "#e69100",
      boxShadow: darkMode
        ? "0 0 0 3px rgba(249, 168, 37, 0.2)"
        : "0 0 0 3px rgba(230, 145, 0, 0.2)",
    },
    "::placeholder": {
      color: darkMode ? "var(--neutral-500)" : "var(--neutral-400)",
      font: "var(--body-sm)",
      opacity: 1,
    },
  };
});

export const InputWithoutIcon = styled("input")(() => {
  const { darkMode } = useThemeStore();

  return {
    width: "100%",
    height: "44px",
    font: "var(--body-md)",
    padding: "var(--spacing-3x)",
    borderRadius: "8px",
    border: darkMode
      ? "1px solid var(--neutral-600)"
      : "1px solid var(--neutral-200)",
    background: darkMode ? "var(--neutral-700)" : "var(--neutral-50)",
    color: darkMode ? "var(--neutralWhite)" : undefined,
    outline: "none",
    transition: "0.3s",
    "&:focus": {
      borderColor: darkMode ? "#f59e0b" : "#e69100",
      boxShadow: darkMode
        ? "0 0 0 3px rgba(249, 168, 37, 0.2)"
        : "0 0 0 3px rgba(230, 145, 0, 0.2)",
    },
    "::placeholder": {
      color: darkMode ? "var(--neutral-500)" : "var(--neutral-400)",
      font: "var(--body-sm)",
      opacity: 1,
    },
  };
});

export const Label = styled("label")(() => {
  const { darkMode } = useThemeStore();

  return {
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "4px",
    fontFamily: '"Lexend", sans-serif',
    color: darkMode ? "var(--neutralWhite)" : "var(--auth-text-gray-dark)",
    textAlign: "left",
  };
});

export const PasswordToggle = styled(IconButton)(() => {
  const { darkMode } = useThemeStore();

  return {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: darkMode ? "var(--neutral-400)" : "var(--auth-text-gray-medium)",
  };
});

// 4) Social Login Button
export const SocialButton = styled(Button)(({ theme }) => {
  const { darkMode } = useThemeStore();

  return {
    width: "100%",
    height: "50px",
    borderRadius: "var(--spacing-3x)",
    border: darkMode
      ? "1px solid var(--neutral-600)"
      : "1px solid rgba(249, 168, 37, 0.4)",
    backgroundColor: darkMode ? "var(--neutral-800)" : undefined,
    color: darkMode ? "var(--neutralWhite)" : undefined,
    textTransform: "none",
    font: "var(--semibold-md)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    "&:hover": {
      boxShadow: darkMode
        ? "0 8px 18px rgba(249, 168, 37, 0.2)"
        : "0 6px 14px rgba(230, 145, 0, 0.18)",
      backgroundColor: darkMode ? "var(--neutral-700)" : undefined,
    },
    [theme.breakpoints.up("xs")]: {
      maxWidth: "100px",
    },
    [theme.breakpoints.up("md")]: {
      maxWidth: "152px",
    },
  };
});

// signin layout

export const SignInMainContainer = styled("div")(() => {
  const { darkMode } = useThemeStore();

  return {
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: darkMode ? "var(--neutral-900)" : undefined,
    p: 2,
    // Dark theme scrollbar styling
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: darkMode
        ? "var(--neutral-800)"
        : "var(--auth-scrollbar-track)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: darkMode
        ? "var(--neutral-600)"
        : "var(--auth-scrollbar-thumb)",
      borderRadius: "4px",
      "&:hover": {
        background: darkMode
          ? "#f59e0b"
          : "var(--auth-scrollbar-thumb-hover)",
      },
    },
    // Firefox scrollbar styling
    scrollbarWidth: "thin",
    scrollbarColor: darkMode
      ? "var(--neutral-600) var(--neutral-800)"
      : "var(--auth-scrollbar-thumb) var(--auth-scrollbar-track)",
  };
});

export const SignInPaperContainer = styled(Paper)(() => {
  const { darkMode } = useThemeStore();

  return {
    backgroundColor: darkMode ? "var(--neutral-800)" : undefined,
    color: darkMode ? "var(--neutralWhite)" : undefined,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
  };
});
